import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { generatePath, useParams } from 'react-router-dom';
import type { SelectChangeEvent } from '@mui/material';
import { useSnackbar } from 'notistack';

import { PermissionGroup } from 'src/constants/modulePermission';
import type { ModulePermission } from 'src/types/modulePermission';
import type { CanEditRole } from 'src/types/user';
import type {
  ModuleRole,
  UserDetail,
  UserDetailUpstream,
} from 'src/types/userModule';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import { routePaths } from 'src/services/routing/routePaths';
import { useConfirm } from 'src/components/app/context/ConfirmContext';
import { useModuleItems } from 'src/components/app/context/ModuleItemsContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import { convertCanEditRoleToRole } from 'src/components/app/context/UserContext/helper';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import {
  initialUserData,
  mapModuleRolesToProps,
  mapUserDetailToProps,
} from 'src/components/userAdmin/UserDetailPage/helper';

export interface FieldErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  business_unit_id?: string;
  is_user_active_in_survey?: string;
  role?: string;
}

export interface UserAdminDetailContextProps {
  loading: boolean;
  fieldErrors: FieldErrors;
  handleFieldChange: (event: SelectChangeEvent<number | string | null>) => void;
  handleRoleChange: (event: SelectChangeEvent<number | null>) => void;
  handleSave: () => Promise<void>;
  user: UserDetail;
  canEditRoles: CanEditRole[];
  userModules: ModuleRole[];
  currentUserModulesPerRole: number[];
  toggleModal: () => void;
  userId: number;
  handleRemoveUser: () => Promise<void>;
  showModal: boolean;
  modulePermissions: ModulePermission[];
  handlePermissionChange: (modulePermission: ModulePermission) => void;
  intersection: ModulePermission[];
  errorMsg: string;
}

export const UserAdminDetailContext =
  createContext<UserAdminDetailContextProps>({
    loading: false,
    fieldErrors: {},
    handleFieldChange: () => {},
    handleRoleChange: () => {},
    handleSave: () => Promise.resolve(),
    user: initialUserData,
    canEditRoles: [],
    userModules: [],
    currentUserModulesPerRole: [],
    toggleModal: () => {},
    userId: initialUserData.id,
    handleRemoveUser: () => Promise.resolve(),
    showModal: false,
    modulePermissions: [],
    handlePermissionChange: () => {},
    intersection: [],
    errorMsg: '',
  });

interface UserAdminDetailProviderProps {
  children: ReactNode;
}

export interface UserAdminDetailHistoryState {
  searchAllSurveys: boolean;
  newUserEmail?: string;
}

export const UserAdminDetailProvider: FC<UserAdminDetailProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation('admin');
  const history = useHistory<UserAdminDetailHistoryState>();
  const { user: admin } = useUserContext();
  const { getPermissionsForGroup } = useUserPermissions();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const {
    copyModules,
    hasAccess,
    readOnly,
    permissionRole,
    orgUnits,
    permissionChecked,
    setPermissionChecked,
  } = useModuleItems();
  const { id: rawUserId } = useParams<{ id: string }>();
  const userId = Number(rawUserId);

  const [user, setUser] = useState(initialUserData);
  const [intersection, setIntersection] = useState<ModulePermission[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [moduleRoles, setModuleRoles] = useState<ModuleRole[]>([]);
  const [userModules, setUserModules] = useState<ModuleRole[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [modulePermissions, setModulePermissions] = useState<
    ModulePermission[]
  >([]);
  const [currentUserModulesPerRole, setcurrentUserModulesPerRole] = useState<
    number[]
  >([]);

  const toggleModal = () => setShowModal((prev) => !prev);

  useEffect(() => {
    if (currentUserModulesPerRole.length === 0 && !!userModules) {
      setcurrentUserModulesPerRole(
        userModules.reduce(
          (acc: number[], userModule) =>
            userModule.module.is_active ? [...acc, userModule.module.id] : acc,
          [],
        ),
      );
    }
  }, [userModules]);

  useEffect(() => {
    setUserModules([]);
    // eslint-disable-next-line array-callback-return
    moduleRoles?.map((moduleRole) => {
      if (user.role && moduleRole.role.id === user.role.id) {
        setUserModules((prev) => [...prev, moduleRole]);
      }
    });
  }, [user.role, moduleRoles]);

  useEffect(() => {
    const newIntersection = modulePermissions.filter((permission) =>
      userModules.find(({ module: m }) => m.id === permission.module.id),
    );
    setIntersection(newIntersection);
  }, [userModules]);

  useEffect(() => {
    const newModulePermissions = modulePermissions.map((item) => {
      const copyUserPermission = copyModules?.find(
        (copyModule) => item.module.name === copyModule,
      );

      if (copyUserPermission) {
        let orgUnitsCopy = orgUnits;
        if (copyUserPermission === 'planAction') {
          orgUnitsCopy = orgUnits.map((unit) => ({
            ...unit,
            read_only: false,
            is_manager_in_charge: false,
            module_permission_org_unit_type: 'PlanActionPermissionOrgUnit',
            plan_action_due_date: null,
          }));
        }

        return {
          ...item,
          has_access: hasAccess,
          read_only: readOnly,
          org_units: orgUnitsCopy,
          permission_group: permissionRole,
        };
      }
      return {
        ...item,
      };
    });

    if (permissionChecked) {
      handleCheckPermission(newModulePermissions);
    }
  }, [copyModules]);

  // Update PermissionCard properties when user permissions changed.
  useEffect(() => {
    // Filter the admins module permissions for modules where they actually are user admins.
    const adminPermissions = getPermissionsForGroup(
      PermissionGroup.ADMIN,
      true,
    );

    // Use the admins module permissions to determine which permission cards should be rendered
    // but use the user's module permissions to fill their content.
    const mergedModulePermissions = adminPermissions?.map((adminPermission) => {
      const matchingUserPermission = user.permissions?.find(
        (userPermission) =>
          userPermission.module.id === adminPermission.module.id,
      );

      return {
        ...adminPermission,
        has_access: matchingUserPermission?.has_access || false,
        read_only: matchingUserPermission?.read_only || false,
        org_units: matchingUserPermission?.org_units || [],
        permission_group:
          matchingUserPermission?.permission_group || PermissionGroup.USER,
      };
    });

    setModulePermissions(mergedModulePermissions);
  }, [user.permissions, getPermissionsForGroup]);

  useEffect(() => {
    // If the passed userId is "0" the detail page will be empty and a new user will be created.
    if (userId === 0) {
      // if we create new user, get the moduleRole
      HttpServiceInstance.get(`${apiPaths.MODULE_ROLE}`, {})
        .then(({ data }) => {
          setModuleRoles(mapModuleRolesToProps(data));
        })
        .catch(() => {
          console.warn('Could not get roles for user.');
        });
    }
    // Get user data if a userId was provided.
    else if (userId) {
      setLoading(true);

      let apiPath = `${apiPaths.ADMIN.USER}${userId}/`;
      if (history.location.state?.searchAllSurveys) {
        apiPath = generatePath(apiPaths.ADMIN.USER_ALL_SURVEYS_DETAIL, {
          id: userId,
        });
      }

      HttpServiceInstance.get(apiPath, {}, true)
        .then(({ data }) => handleUserData(data))
        .finally(() => setLoading(false));
    }
  }, [userId]);

  const handleUserData = useCallback((userData: UserDetailUpstream) => {
    const { business_unit_id } = userData;

    setUser(
      mapUserDetailToProps({
        ...userData,
        business_unit_id: business_unit_id || initialUserData.business_unit_id,
      }),
    );
    setUserModules([]);

    HttpServiceInstance.get(`${apiPaths.MODULE_ROLE}`, {})
      .then(({ data }) => {
        setModuleRoles(mapModuleRolesToProps(data));
      })
      .catch((_) => {
        setErrorMsg('Could not get roles for user.');
      });
  }, []);

  /**
   * Handle value changes for input fields within the "meta" block.
   */
  const handleFieldChange = (
    event: SelectChangeEvent<number | string | null>,
  ) => {
    const {
      target: { name: fieldName, value },
    } = event;
    setUser((prevUserState) => ({
      ...prevUserState,
      [fieldName]: value,
    }));
  };

  const handleRoleChange = (event: SelectChangeEvent<number | null>) => {
    if (!admin) return;

    const {
      target: { value },
    } = event;
    const newUserRole = admin.role?.canEditRoles.find(
      ({ id }) => String(id) === String(value),
    );
    setUser((prevUserState) => ({
      ...prevUserState,
      role: newUserRole ? convertCanEditRoleToRole(newUserRole) : null,
    }));
  };

  const handlePermissionChange = (modulePermission: ModulePermission) => {
    const mergedPermissions = [modulePermission, ...user.permissions].reduce(
      (acc: ModulePermission[], permission) =>
        acc.find(({ module }) => module.id === permission.module.id) ||
        // Strip irrelevant permissions (no access, no orgUnit and no permissionGroup selected).
        (!permission.has_access &&
          !permission.read_only &&
          !permission.org_units.length &&
          !permission.permission_group)
          ? acc
          : [...acc, permission],
      [],
    );

    setUser({ ...user, permissions: mergedPermissions });
  };

  const handleCheckPermission = (permissions: ModulePermission[]) => {
    const endpoint = generatePath(apiPaths.ADMIN.CHECK_USER, {
      id: userId,
    });
    setFieldErrors({});
    setLoading(true);

    const checkUserRequest = HttpServiceInstance.patch(endpoint, {
      permissions: permissions,
    });

    setPermissionChecked(false);
    checkUserRequest
      .then((_) => {
        setModulePermissions(permissions);
        setUser({ ...user, permissions: permissions });
        enqueueSnackbar(t('admin:Detail.CopyModule.permissionChanged'), {
          variant: 'success',
        });
      })
      .catch(() => {
        setErrorMsg(
          'You are not allowed to copy this permission' +
            ' because it is not in the area of responsibility for the selected module.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSave = () => {
    setShowModal(false);
    setFieldErrors({});
    setLoading(true);

    let generatedApiPath = `${apiPaths.ADMIN.USER}${userId}/`;
    if (history.location.state?.searchAllSurveys) {
      generatedApiPath = generatePath(apiPaths.ADMIN.USER_UPDATE_NEW_SURVEY, {
        id: userId,
      });
    }

    const updateUserRequest = userId
      ? HttpServiceInstance.put(generatedApiPath, user)
      : HttpServiceInstance.post(apiPaths.ADMIN.USER, user);

    return updateUserRequest
      .then((response) => {
        enqueueSnackbar(
          t(
            response.status === 200
              ? 'Detail.UserAddedToSurvey'
              : 'Detail.UserSaved',
          ),
          {
            variant: 'success',
          },
        );

        if (userId) {
          // Update detail with new data.
          const updatedUserModulesPerRole = response.data?.permissions.reduce(
            (acc: number[], permission: ModulePermission) =>
              permission.module.is_active &&
              userModules.some(
                (userModule) => userModule.module.id === permission.module.id,
              )
                ? [...acc, permission.module.id]
                : acc,
            [],
          );
          if (updatedUserModulesPerRole)
            setcurrentUserModulesPerRole(updatedUserModulesPerRole);
          handleUserData(response.data);
        } else {
          // Redirect to newly created user detail page.
          history.push(
            generatePath(routePaths.USER_ADMIN_DETAIL, {
              id: response.data.id,
            }),
          );
        }
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          setFieldErrors(error.response.data);
        } else {
          throw error;
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRemoveUser = async () => {
    await confirm({
      description: 'admin:Detail.Delete.Warning',
      title: 'admin:Detail.Delete.Headline',
    })
      .then(async () => {
        setLoading(true);
        await HttpServiceInstance.delete(
          `${apiPaths.ADMIN.USER}${userId}/`,
          {},
        );
      })
      .then(() => history.push(routePaths.USER_ADMIN_LIST))
      .catch(() => setLoading(false));
  };

  const canEditRoles = useMemo(
    () => (admin && admin.role?.canEditRoles) || [],
    [admin],
  );

  return (
    <UserAdminDetailContext.Provider
      value={{
        canEditRoles,
        currentUserModulesPerRole,
        loading,
        errorMsg,
        fieldErrors,
        handleRemoveUser,
        handleFieldChange,
        handlePermissionChange,
        handleRoleChange,
        handleSave,
        intersection,
        modulePermissions,
        showModal,
        toggleModal,
        user,
        userId,
        userModules,
      }}
    >
      {children}
    </UserAdminDetailContext.Provider>
  );
};

const useUserDetail = () => useContext(UserAdminDetailContext);

export default useUserDetail;
